import { Grid } from '@mui/material';
import { dateFormat, pastDateTime } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';

import { Check, Close } from '@mui/icons-material';
import { Language } from '@nx-smartmonkey/shared/domain';
import * as routing from '../../../constants/Routing';
import { UserBase, UserBaseValidation } from '../../../domain/backoffice/user/UserBase';
import Text from '../../../react-components/Text';
import { AlignmentTypes, DataTableColumnProps } from '../../../react-components/data-table/DataTable';

type GetUsersTableColumnsProps = {
  navigate: (destinationUrl: string) => void;
};

export const getUsersTableColumns = ({ navigate }: GetUsersTableColumnsProps) => {
  return [
    {
      width: 200,
      height: 25,
      sortable: true,
      accessorName: `name`,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.name" />,
      formatter: ({ doc: user }: { doc: UserBase }) => {
        const name = `${user.first_name} ${user.last_name}`;
        const deleted = user.deleted;

        return (
          <Grid container direction="row" alignItems="center">
            {name && name !== `` ? (
              <RoutalTooltip title={name}>
                <Text
                  variant="button"
                  onClick={(e: any) => {
                    navigate(`${routing.BACKOFFICE.CLIENTS.USERS}/details/${user.id}`);
                  }}
                  style={{
                    fontWeight: 500,
                    ...(deleted ? { textDecoration: `line-through` } : {}),
                  }}
                >
                  {name.length > 35 ? `${name.substring(0, 35)}...` : `${name}`}
                </Text>
              </RoutalTooltip>
            ) : (
              <Text
                variant="button"
                onClick={(e: any) => {
                  navigate(`${routing.BACKOFFICE.CLIENTS.USERS}/details/${user.id}`);
                }}
                style={{
                  fontWeight: 500,
                  ...(deleted ? { textDecoration: `line-through` } : {}),
                  color: RoutalPalette.secondary00,
                }}
                intlId="no_name"
              />
            )}
          </Grid>
        );
      },
    },
    {
      width: 240,
      accessorName: `email`,
      align: `center` as AlignmentTypes,
      sortable: false,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.email" />,
      formatter: ({ value }: { value?: string }) =>
        value && (
          <RoutalTooltip title={value}>
            <Text variant="body" style={{ maxWidth: `220px`, cursor: `copy`, userSelect: `none` }}>
              {value}
            </Text>
          </RoutalTooltip>
        ),
    },
    {
      width: 120,
      accessorName: `phone`,
      align: `center` as AlignmentTypes,
      sortable: false,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.phone" />,
      formatter: ({ value }: { value?: string }) =>
        value && (
          <Text variant="body" style={{ maxWidth: `220px`, userSelect: `none` }}>
            {value}
          </Text>
        ),
    },
    {
      width: 120,
      accessorName: `validation`,
      align: `center` as AlignmentTypes,
      sortable: false,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.validation" />,
      formatter: ({ value: validation }: { value: UserBaseValidation }) =>
        validation.email_validated ? <Check style={{ color: `#70D292` }} /> : <Close style={{ color: `#E03030` }} />,
    },
    {
      width: 120,
      accessorName: `language`,
      align: `center` as AlignmentTypes,
      sortable: false,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.language" />,
      formatter: ({ value: language }: { value: Language }) =>
        language && (
          <Text
            variant="body"
            style={{ maxWidth: `220px`, userSelect: `none` }}
            intlId={`settings.account.profile.${language.value}`}
          />
        ),
    },
    {
      width: 60,
      accessorName: `created_at`,
      align: `center` as AlignmentTypes,
      sortable: true,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.created_at" />,
      formatter: ({ value: createdAt }) => (
        <RoutalTooltip title={dateFormat(createdAt)}>
          <Text variant="body">{pastDateTime(createdAt)}</Text>
        </RoutalTooltip>
      ),
    } as DataTableColumnProps,
  ];
};
